import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import type {
  AmericanFootballFixture,
  AmericanFootballPeriod,
  AmericanFootballQuarterScore
} from '@ps/web-shared-generated-graphql/sports';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventId: EventId;
  eventTime: string;
  americanFootballFixture?: AmericanFootballFixture;
}

const standardPeriods = [
  'PERIOD_1',
  'PERIOD_2',
  'PERIOD_3',
  'PERIOD_4'
] as const satisfies AmericanFootballPeriod[];

const isAmericanFootballQuarterScore = (
  quarterScore: AmericanFootballQuarterScore | null
): quarterScore is AmericanFootballQuarterScore =>
  notEmpty(quarterScore) && notEmpty(quarterScore.period) && notEmpty(quarterScore.score);

const getPeriodScores = (
  quarterScores: AmericanFootballFixture['quarterScores']
): AmericanFootballQuarterScore[] => {
  const standardPeriodScores = standardPeriods.map(
    (period) => quarterScores?.find((qs) => qs?.period === period) ?? { period }
  );

  const additionalPeriodScores =
    quarterScores?.filter((qs) => !standardPeriodScores.find((sp) => sp.period === qs?.period)) ??
    [];

  return [
    ...standardPeriodScores,
    ...additionalPeriodScores.filter(isAmericanFootballQuarterScore)
  ];
};

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: AmericanFootballFixture['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const AmericanFootball: React.FC<Props> = ({
  enrichedParticipants,
  americanFootballFixture,
  eventId,
  eventTime
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];

  const currentScore =
    participantA.type && participantB.type ? americanFootballFixture?.score : null;
  const quarterScores =
    participantA.type && participantB.type ? americanFootballFixture?.quarterScores : null;

  const hasPeriodScores = notEmpty(quarterScores);
  const periodScores = getPeriodScores(quarterScores);

  return (
    <ScoreboardWrapper
      eventId={eventId}
      eventTime={eventTime}
      eventTypeId={EventTypeId.AMERICAN_FOOTBALL}
    >
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant name={participantA.participant} />
          <ScoreboardParticipant name={participantB.participant} />
        </ScoreboardParticipants>
        <ScoreboardScores>
          {hasPeriodScores
            ? periodScores.map((periodScore) => (
                <ScoreboardScore
                  key={periodScore.period}
                  score={[
                    getParticipantScore(participantA, periodScore.score) ?? null,
                    getParticipantScore(participantB, periodScore.score) ?? null
                  ]}
                />
              ))
            : null}
          <ScoreboardScore
            score={[
              getParticipantScore(participantA, currentScore) ?? null,
              getParticipantScore(participantB, currentScore) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.AMERICAN_FOOTBALL, [
              currentScore?.home ?? 0,
              currentScore?.away ?? 0
            ])}
          />
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default AmericanFootball;
